import { useCallback, useEffect, useMemo, useState, FC, ComponentType } from 'react';
import { useNavigate } from 'react-router-dom';

// material-ui
import { useTheme } from '@mui/material/styles';
import { Box, Button, Divider, List, Tooltip, Typography, useMediaQuery } from '@mui/material';

// project import
import NavItem from './NavItem';
import NavGroup from './NavGroup';
import menuItem from 'menu-items';
import superAdminMenu from 'menu-items/super-admin';
import consultantMenu from 'menu-items/consultant';

import useConfig from 'hooks/useConfig';
import useAuth from 'hooks/useAuth';
import { BASE_URL, HORIZONTAL_MAX_ITEM, MenuOrientation } from 'config';
import { useGetMenuMaster } from 'api/menu';
import {
  DASHBOARD_MODE_EVENT,
  DashboardModes,
  getDashboardMode,
  setDashboardMode as persistDashboardMode,
  CONSULTANT_SESSION_SELECTED_CITY_KEY,
  DashboardMode
} from 'utils/dashboardMode';

import IconButton from 'components/@extended/IconButton';
import AppstoreOutlined from '@ant-design/icons/AppstoreOutlined';
import UserSwitchOutlined from '@ant-design/icons/UserSwitchOutlined';
import CrownOutlined from '@ant-design/icons/CrownOutlined';

interface MenuItem {
  id: string;
  title?: string;
  caption?: string;
  icon?: any;
  type: 'group' | 'item' | 'collapse';
  url?: string;
  children?: MenuItem[];
  target?: boolean;
  disabled?: boolean;
  breadcrumbs?: boolean;
  external?: boolean;
  chip?: any;
}

interface MenuTemplate {
  items: MenuItem[];
}

interface AccessRegistry {
  [key: string]: boolean;
}

interface UserInformation {
  id?: string;
  email?: string;
  userType?: 'SuperAdmin' | 'Consultant' | 'User' | string;
  city?: any;
  cityName?: string;
  state?: string;
  country?: string;
  Consultant?: { isConsultant?: boolean };
  isConsultant?: boolean;
  access?: AccessRegistry;
}

interface NavGroupItemProps {
  title?: string;
  elements?: MenuItem[];
  icon?: any;
  url?: string;
}

interface AuthContextType {
  user: UserInformation | null;
}
const useAuthHook = useAuth as () => AuthContextType;

const parseJSONSafe = (value: string | null, fallback: any = null): any => {
  try {
    return value ? JSON.parse(value) : fallback;
  } catch (error) {
    console.warn('Failed to parse JSON value from storage', error);
    return fallback;
  }
};

const collectMenuKeys = (items: MenuItem[], registry: AccessRegistry = {}): AccessRegistry => {
  items.forEach((item) => {
    if (item.id) registry[item.id] = true;
    if (item.url) {
      const key = item.url.replace(/^\//, '').split('/')[0];
      if (key) registry[key] = true;
    }
    if (item.children) collectMenuKeys(item.children, registry);
  });
  return registry;
};

const filterMenuByAccess = (items: MenuItem[], access: AccessRegistry): MenuItem[] =>
  items
    .map((item) => {
      if (item.children) {
        const filteredChildren = filterMenuByAccess(item.children, access);
        if (filteredChildren.length) return { ...item, children: filteredChildren };
      }
      if (access[item.id] === false) return null;
      return item;
    })
    .filter((item): item is MenuItem => item !== null);

const getMenuTemplateForMode = (mode: any): MenuTemplate => {
  switch (mode) {
    case DashboardModes.SUPER_ADMIN:
      return superAdminMenu as MenuTemplate;
    case DashboardModes.CONSULTANT:
      return consultantMenu as MenuTemplate;
    default:
      return menuItem as MenuTemplate;
  }
};

const buildMenuItems = (mode: any, access: AccessRegistry = {}): MenuTemplate => {
  const template: MenuTemplate = getMenuTemplateForMode(mode);
  if (!template?.items) return { items: [] };
  if (mode === DashboardModes.CITY) {
    const safeAccess = access && typeof access === 'object' ? access : {};
    return { ...template, items: filterMenuByAccess(template.items, safeAccess) };
  }
  return { ...template, items: [...template.items] };
};

const readStoredAccess = (): AccessRegistry =>
  typeof window === 'undefined' ? {} : parseJSONSafe(window.localStorage.getItem('userAccess'), {});

const readStoredUser = (): UserInformation | null =>
  typeof window === 'undefined' ? null : (parseJSONSafe(window.localStorage.getItem('userInformation'), null) as UserInformation | null);

// ==============================|| DRAWER CONTENT - NAVIGATION (REFAC) ||============================== //

const Navigation: FC = () => {
  const theme = useTheme();
  const { menuOrientation } = useConfig();
  const { menuMaster }: any = useGetMenuMaster();
  const drawerOpen = menuMaster?.isDashboardDrawerOpened ?? true;
  const downLG = useMediaQuery(theme.breakpoints.down('lg'));
  const { user } = useAuthHook();
  const navigate = useNavigate();

  // reactive local states
  const [storedUserState, setStoredUserState] = useState<UserInformation | null>(() => readStoredUser());
  const [accessRegistry, setAccessRegistry] = useState<AccessRegistry>(() => readStoredAccess());
  const [dashboardMode, setDashboardModeState] = useState<any>(() => {
    const initialAllowed = (() => {
      const su = storedUserState;
      const isConsultant = Boolean(su?.Consultant?.isConsultant || su?.isConsultant || su?.userType === 'Consultant');
      const isSuperAdmin = su?.userType === 'SuperAdmin';
      const hasCity = Boolean(su?.city);
      if (isSuperAdmin && !hasCity) return DashboardModes.SUPER_ADMIN;
      if (isConsultant && !hasCity) return DashboardModes.CONSULTANT;
      return getDashboardMode() || DashboardModes.CITY;
    })();
    return initialAllowed;
  });

  const [selectedItems, setSelectedItems] = useState<string>('');
  const [selectedLevel, setSelectedLevel] = useState<number>(0);
  const [menuItems, setMenuItems] = useState<MenuTemplate>(() =>
    buildMenuItems(getDashboardMode() || DashboardModes.CITY, readStoredAccess())
  );

  const activeUser = (user as UserInformation) || storedUserState || null;
  const storedUserWithCity = storedUserState && storedUserState.city ? storedUserState : null;
  const activeUserSafe = storedUserWithCity || activeUser || {};
  const isSuperAdmin = activeUserSafe?.userType === 'SuperAdmin';
  const isConsultant = Boolean(
    activeUserSafe?.Consultant?.isConsultant || activeUserSafe?.isConsultant || activeUserSafe?.userType === 'Consultant'
  );
  const selectedThisSession = typeof window === 'undefined' ? null : window.sessionStorage.getItem(CONSULTANT_SESSION_SELECTED_CITY_KEY);
  const hasActiveCityRaw = Boolean(activeUserSafe?.city);
  const hasActiveCity = isConsultant ? Boolean(selectedThisSession && hasActiveCityRaw) : hasActiveCityRaw;

  const allowedModes = useMemo(() => {
    const modes = new Set<any>();
    if (isSuperAdmin) modes.add(DashboardModes.SUPER_ADMIN);
    if (isConsultant) modes.add(DashboardModes.CONSULTANT);
    if (hasActiveCity || (!modes.size && !isSuperAdmin && !isConsultant)) modes.add(DashboardModes.CITY);
    if (!modes.size) modes.add(DashboardModes.CITY);
    return modes;
  }, [isSuperAdmin, isConsultant, hasActiveCity]);

  const normalizeMode = useCallback(
    (mode: any | null): any => {
      if (!mode || !allowedModes.has(mode)) return DashboardModes.CITY;
      if (mode === DashboardModes.CITY && !hasActiveCity) return DashboardModes.CITY;
      return mode;
    },
    [allowedModes, hasActiveCity]
  );

  const updateDashboardMode = useCallback(
    (mode: any | null, { persist = true } = {}): any => {
      const normalized = normalizeMode(mode);
      setDashboardModeState((current: any) => (current === normalized ? current : normalized));
      if (persist) persistDashboardMode(normalized);
      return normalized;
    },
    [normalizeMode]
  );

  useEffect(() => {
    if (dashboardMode === DashboardModes.CITY) {
      setMenuItems(buildMenuItems(DashboardModes.CITY, accessRegistry));
    } else {
      setMenuItems(buildMenuItems(dashboardMode));
    }
  }, [dashboardMode, accessRegistry]);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const serviceToken = window.localStorage.getItem('serviceToken');
    if (!serviceToken) return;

    const fetchAccess = async () => {
      try {
        const res = await fetch(`${BASE_URL}/api/user-info`, {
          method: 'GET',
          headers: { Authorization: `Bearer ${serviceToken}` }
        });

        if (!res.ok) return;
        let data: UserInformation = await res.json();

        try {
          const isConsultantFlag = Boolean(data?.Consultant?.isConsultant || data?.isConsultant || data?.userType === 'Consultant');
          const selectedThisSessionLoc = window.sessionStorage.getItem(CONSULTANT_SESSION_SELECTED_CITY_KEY);
          if (isConsultantFlag && !selectedThisSessionLoc) {
            if (data && typeof data === 'object') {
              delete data.city;
              delete data.cityName;
              delete data.state;
              delete data.country;
            }
          }
        } catch (e) {
          console.warn('Failed to sanitize user info for session', e);
        }

        window.localStorage.setItem('userInformation', JSON.stringify(data));
        setStoredUserState(data);

        const templateForKeys = hasActiveCity ? menuItem : getMenuTemplateForMode(DashboardModes.CITY);
        const defaultAccess = collectMenuKeys(templateForKeys.items || [], {});
        const incomingAccess = data?.access && typeof data.access === 'object' ? data.access : {};
        const mergedAccess: AccessRegistry = { ...defaultAccess, ...(incomingAccess as AccessRegistry) };

        window.localStorage.setItem('userAccess', JSON.stringify(mergedAccess));
        setAccessRegistry(mergedAccess);

        window.dispatchEvent(new Event('menuAccessUpdated'));
      } catch (err) {
        console.error('Error fetching user info:', err);
      }
    };

    fetchAccess();
  }, []); 

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handler = () => {
      const latestUser = parseJSONSafe(window.localStorage.getItem('userInformation'), null) as UserInformation | null;
      const latestAccess = parseJSONSafe(window.localStorage.getItem('userAccess'), {}) as AccessRegistry;
      setStoredUserState(latestUser);
      setAccessRegistry(latestAccess);
    };

    window.addEventListener('menuAccessUpdated', handler);
    return () => window.removeEventListener('menuAccessUpdated', handler);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const onMode = (event: Event) => {
      const custom = event as CustomEvent<{ mode: any }>;
      const requested = (custom?.detail?.mode as any) ?? getDashboardMode();
      const normalized = normalizeMode(requested);
      if (normalized !== dashboardMode) {
        setDashboardModeState(normalized);
      }
    };

    window.addEventListener(DASHBOARD_MODE_EVENT, onMode as EventListener);
    return () => window.removeEventListener(DASHBOARD_MODE_EVENT, onMode as EventListener);
  }, [dashboardMode, normalizeMode]);

  // prepare nav groups render
  const isHorizontal = menuOrientation === MenuOrientation.HORIZONTAL && !downLG;
  const lastItem: number | null = isHorizontal ? HORIZONTAL_MAX_ITEM : null;
  let lastItemIndex = menuItems.items.length - 1;
  let remItems: NavGroupItemProps[] = [];
  let lastItemId: string | undefined;

  if (lastItem && lastItem < menuItems.items.length) {
    lastItemId = menuItems.items[lastItem - 1].id;
    lastItemIndex = lastItem - 1;
    remItems = menuItems.items.slice(lastItem - 1).map((item) => ({
      title: item.title,
      elements: item.children,
      icon: item.icon,
      ...(item.url && { url: item.url })
    }));
  }

  const navGroups = menuItems.items.slice(0, lastItemIndex + 1).map((item, index) => {
    if (item.type === 'group') {
      if (item.url && item.id !== lastItemId) {
        return (
          <List key={item.id} {...(isHorizontal && { sx: { mt: 0.5 } })}>
            {!isHorizontal && index !== 0 && <Divider sx={{ my: 0.5 }} />}
            <NavItem item={item} level={1} isParents />
          </List>
        );
      }
      return (
        <NavGroup
          key={item.id}
          setSelectedItems={setSelectedItems}
          setSelectedLevel={setSelectedLevel}
          selectedLevel={selectedLevel}
          selectedItems={selectedItems}
          lastItem={lastItem}
          remItems={remItems}
          lastItemId={lastItemId}
          item={item}
        />
      );
    }

    return (
      <Typography key={item.id} variant="h6" color="error" align="center">
        Fix - Navigation Group
      </Typography>
    );
  });

  const renderDashboardSwitch = (
    label: string,
    mode: DashboardMode,
    href: string,
    IconComponent: ComponentType<Record<string, unknown>>
  ) => {
    const isActive = dashboardMode === mode;

    const handleClick = () => {
      const normalized = updateDashboardMode(mode);
      persistDashboardMode(normalized);
      navigate(href);
    };

    if (drawerOpen) {
      return (
        <Button fullWidth variant={isActive ? 'contained' : 'outlined'} size="small" onClick={handleClick}>
          {label}
        </Button>
      );
    }

    return (
      <Tooltip title={label} placement="right">
        <Box>
          <IconButton
            onClick={handleClick}
            color={isActive ? 'primary' : 'secondary'}
            variant={isActive ? 'light' : 'text'}
            sx={{
              width: 44,
              height: 44,
              borderRadius: 1.25,
              border: '1px solid',
              borderColor: isActive ? 'primary.main' : 'transparent',
              bgcolor: isActive ? 'primary.lighter' : 'transparent',
              color: isActive ? 'primary.main' : 'text.secondary',
              '&:hover': {
                bgcolor: isActive ? 'primary.light' : 'action.hover',
                borderColor: isActive ? 'primary.main' : 'action.hover'
              }
            }}
          >
            <IconComponent />
          </IconButton>
        </Box>
      </Tooltip>
    );
  };

  return (
    <Box
      sx={{
        pt: drawerOpen ? (isHorizontal ? 0 : 2) : 0,
        ...(!isHorizontal && { '& > ul:first-of-type': { mt: 0 } }),
        display: isHorizontal ? { xs: 'block', lg: 'flex' } : 'block'
      }}
    >
      {!isHorizontal && (isConsultant || isSuperAdmin) && (
        <Box
          sx={{
            px: drawerOpen ? 2.5 : 1,
            pb: 1.5,
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            alignItems: drawerOpen ? 'stretch' : 'center'
          }}
        >
          {hasActiveCity && renderDashboardSwitch('Client Dashboard', DashboardModes.CITY, '/overview', AppstoreOutlined)}
          {isConsultant && renderDashboardSwitch('Consultant Dashboard', DashboardModes.CONSULTANT, '/consultant', UserSwitchOutlined)}
          {isSuperAdmin && renderDashboardSwitch('Super Admin Dashboard', DashboardModes.SUPER_ADMIN, '/super-admin', CrownOutlined)}
          <Divider sx={{ mt: 0.5 }} />
        </Box>
      )}
      {navGroups}
    </Box>
  );
};

export default Navigation;
